// What every signed-in user sees of a company's published trust score (DESIGN.md, the trust score; .ai/data-model-and-policy.md, Access): a traffic light
// in the overall's band, with the overall and at most one state on hover, never a breakdown. Pure, so the unit tests import it.

export type ScoreBand = 'high' | 'mid' | 'low';

// The fields of a companyTrustScore row the display reads. The public read projects only these, and of a suppressed row only
// its decision and public reason; support and admin viewers read the whole row, which is why the publishDecision checks below
// matter.
export interface TrustScoreRow {
  id?: string;
  companyId?: string | null;
  manualReview?: {
    status?: string | null;
    publishDecision?: string | null;
  } | null;
  published?: {
    publishedAt?: string | Date | null;
    overall?: number | null;
    killSwitchActive?: boolean | null;
    hmrcReviewExpired?: boolean | null;
    suppressedReason?: string | null;
  } | null;
}

export type PublicTrustScore =
  | { kind: 'none' }
  // Support suppressed it (or reopened its review and hasn't decided yet, `reopened`): shown as 0 with its public reason, and
  // nothing else
  | { kind: 'suppressed'; reason: string | null; reopened?: true }
  | {
      kind: 'scored';
      overall: number;
      band: ScoreBand;
      publishedAt: string;
      underReview: boolean;
      hmrcOverdue: boolean;
      killSwitch: boolean;
    };

export const NO_TRUST_SCORE: PublicTrustScore = { kind: 'none' };

const clampScore = (score: number) => Math.round(Math.min(Math.max(score, 0), 100));

/** 85 and up is high (green), 65 and up mid (amber), anything lower low (red). */
export function scoreBand(score: number): ScoreBand {
  const clamped = clampScore(score);
  if (clamped >= 85) return 'high';
  if (clamped >= 65) return 'mid';
  return 'low';
}

const publishedAtText = (value: unknown): string | null => {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  return typeof value === 'string' && value ? value : null;
};

/** The public view of a companyTrustScore row, or "none" when no row or nothing published. */
export function toPublicTrustScore(row: TrustScoreRow | null | undefined): PublicTrustScore {
  if (!row) return NO_TRUST_SCORE;
  const decision = row.manualReview?.publishDecision;
  // A suppressed score whose review support reopened (REOPENED) still shows as suppressed until support publishes it
  if (decision === 'SUPPRESS' || decision === 'REOPENED') {
    const reason = row.published?.suppressedReason;
    return {
      kind: 'suppressed',
      reason: typeof reason === 'string' && reason.trim() ? reason.trim() : null,
      ...(decision === 'REOPENED' ? { reopened: true as const } : {}),
    };
  }
  const overall = row.published?.overall;
  const publishedAt = publishedAtText(row.published?.publishedAt);
  if (decision !== 'PUBLISH' || !publishedAt || typeof overall !== 'number' || !Number.isFinite(overall)) {
    return NO_TRUST_SCORE;
  }
  return {
    kind: 'scored',
    overall: clampScore(overall),
    band: scoreBand(overall),
    publishedAt,
    underReview: row.manualReview?.status === 'NOT_REVIEWED',
    hmrcOverdue: row.published?.hmrcReviewExpired === true,
    killSwitch: row.published?.killSwitchActive === true,
  };
}

/**
 * The companyTrustScore id in a `dataservice:loadById` event's detail, or null for any other collection. crag dispatches the
 * event when a realtime change reaches a row it has never loaded (a first publish, or a suppress of a never-published company)
 * and then loads the row silently, so the trust-score service follows these events to hear of it.
 */
export function loadedTrustScoreDocId(detail: unknown): string | null {
  if (!detail || typeof detail !== 'object') return null;
  const { schemaName, id } = detail as { schemaName?: unknown; id?: unknown };
  return schemaName === 'companyTrustScore' && typeof id === 'string' && id ? id : null;
}

/** The number shown, or null when there is none: a suppressed score shows 0. */
export function displayedScore(score: PublicTrustScore): number | null {
  if (score.kind === 'scored') return score.overall;
  return score.kind === 'suppressed' ? 0 : null;
}

/** The band of the number shown, or null when there is none. */
export function displayedBand(score: PublicTrustScore): ScoreBand | null {
  if (score.kind === 'scored') return score.band;
  return score.kind === 'suppressed' ? 'low' : null;
}

const dateFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export const formatPublishedDate = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'on an unknown date' : dateFormat.format(date);
};

/** What a surface knows of a company's score: the public view, or that it is still loading or its read failed. */
export type TrustScoreReading = PublicTrustScore | 'loading' | 'failed';

/** The traffic light's colour: the shown number's band, so a suppressed score is red, and none for no score or no reading. */
export type ScoreLightTone = ScoreBand | 'none';

export function scoreLightTone(reading: TrustScoreReading): ScoreLightTone {
  return typeof reading === 'string' ? 'none' : (displayedBand(reading) ?? 'none');
}

/**
 * Everything the light says, shown only on hover or focus and given as its accessible name. The number is not shown yet (the
 * light carries the band): a scored company gets "Trust score", at most one state, the most serious that is true (a failed
 * critical check, then an overdue HMRC check, then a review), then always "Published <date>": stacking states would read as a
 * breakdown. A suppressed company gets "Trust score" and its public reason, and no score gets "No trust score yet".
 */
export function scoreHoverLines(
  reading: TrustScoreReading,
  formatDate: (value: string) => string = formatPublishedDate,
): string[] {
  if (reading === 'loading') return ['Loading the trust score'];
  if (reading === 'failed') return ['Trust score unavailable'];
  if (reading.kind === 'none') return ['No trust score yet'];
  if (reading.kind === 'suppressed') return ['Trust score', ...(reading.reason ? [reading.reason] : [])];
  const state = reading.killSwitch
    ? 'Failed a critical check'
    : reading.hmrcOverdue
      ? 'HMRC check overdue'
      : reading.underReview
        ? 'Under review'
        : null;
  return ['Trust score', ...(state ? [state] : []), `Published ${formatDate(reading.publishedAt)}`];
}

// The connections graph's risk is the inverse of the score's band
export const BAND_RISK_CLASS = {
  high: 'risk-low',
  mid: 'risk-medium',
  low: 'risk-high',
} as const satisfies Record<ScoreBand, string>;
